import {
  useGetRevisionsQuery,
  useRevertRecordingMutation,
} from '@carboncopyco/data';
import { RevertOutlineIcon } from '@carboncopyco/icons';
import {
  PermissionAction,
  PermissionKey,
  type Revision,
} from '@carboncopyco/types';
import { Button, ConfirmAlert, notify } from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import { useState } from 'react';
import { usePermissionsContext } from '../../../utils';
import { useClonePageProps } from '../ClonePageContext';

export const RevertButton = ({ revision }: { revision: Revision }) => {
  const { recordId, currRevision } = revision;
  const [openRevertModal, setOpenRevertModal] = useState(false);
  const [isDisabled, setIsDisabled] = useBoolean();
  const { organizationId, cloneId } = useClonePageProps();
  const { data } = useGetRevisionsQuery({
    organizationId,
    sourceType: 'record',
    id: String(cloneId),
    reverseOrder: true,
  });
  const revisions = data?.items;
  const isLastRevision = revisions?.length === currRevision + 1;
  const { getPermissions } = usePermissionsContext();
  const [revertRecording] = useRevertRecordingMutation();

  if (isLastRevision) {
    return null;
  }

  const revertClonePermission = getPermissions(
    PermissionKey.record,
    PermissionAction.revertRecord
  );
  return (
    <div className="flex flex-row">
      {revertClonePermission && (
        <Button
          dark
          icon={<RevertOutlineIcon />}
          onClick={() => setOpenRevertModal(true)}
        />
      )}
      <ConfirmAlert
        disabled={isDisabled}
        title="Revert to old revision?"
        containerClass="w-ds-modal-560"
        onConfirm={() => {
          setIsDisabled.setTrue();
          revertRecording({
            organizationId,
            revisionId: String(currRevision),
            id: recordId,
          })
            .unwrap()
            .then(() => {
              notify.success('Success reverting clone');
            })
            .catch(() => {
              notify.error('Error reverting clone');
            })
            .finally(() => {
              setOpenRevertModal(false);
              setIsDisabled.setFalse();
            });
        }}
        onClose={() => setOpenRevertModal(false)}
        show={openRevertModal}
        description={`A new revision of this clone will be created with the content of revision #${
          currRevision + 1
        }`}
        noSecondButton
        yesLabel="Revert"
      />
    </div>
  );
};
